'use client';

import { createContext, ReactNode, useCallback, useContext } from "react";
import {
    PublicationStatus,
    ResearchBlogEntry,
    ResearchBlogEntryInput,
} from "@/app/lib/generated/graphql-types";
import {
    useSingleTypeEditor,
    UseSingleTypeEditorType,
} from "@/app/lib/hooks/use-editor/useSingleTypeEditor";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";
import { toggleDraftMode } from "@/app/lib/actions/editing";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import { getResearchBlogEntryDetail } from "@/app/features/sites/research/data-access/queries";
import {
    researchDetailInputFrom,
    updateResearchDetail,
} from "@/app/features/sites/research/data-access/mutations";
import { ResearchDetailSchema } from "@/app/features/sites/research/types/schema";
import type { ResearchDetailEditable } from "@/app/features/sites/research/types/types";
import { notNull } from "@/app/lib/common/not-null";

const SiteResearchDetailEditorContext = createContext<
    UseSingleTypeEditorType<ResearchDetailEditable> | undefined
>(undefined);

export function useSiteResearchDetailEditor() {
    const context = useContext(SiteResearchDetailEditorContext);
    if (!context) {
        throw new Error("useSiteResearchDetailEditor must be used within SiteResearchDetailEditorProvider");
    }
    return context;
}

function editableFromResearchBlogEntry(entry: ResearchBlogEntry): ResearchDetailEditable {
    return {
        id: entry.documentId,
        title: entry.title,
        subtitle: entry.subtitle,
        excerpt: entry.excerpt,
        publications: entry.publications,
        image: entry.image,
        details: entry.details.filter(notNull).map(detail => ({
            id: detail.id,
            viewVariant: detail.viewVariant,
            title: detail.title,
            subtitle: detail.subtitle,
            description: detail.description,
            image: detail.image,
            imagePosition: detail.imagePosition,
            splitInitial: detail.splitInitial,
            splitMax: detail.splitMax,
            links: detail.links?.filter(notNull),
            documents: detail.documents?.filter(notNull),
        })),
    };
}

interface SiteResearchDetailEditorProviderProps {
    children: ReactNode;
    documentId: string;
    isDraftModeEnabled: boolean;
}

export default function SiteResearchDetailEditorProvider({
    children,
    documentId,
    isDraftModeEnabled,
}: SiteResearchDetailEditorProviderProps) {
    const loadEntity = useCallback(
        ({ status }: { status: PublicationStatus }) => getResearchBlogEntryDetail({ documentId, status }),
        [documentId]
    );
    const update = useCallback(
        ({ data, status }: { data: ResearchBlogEntryInput; status: PublicationStatus }) => (
            updateResearchDetail({ documentId, data, status })
        ),
        [documentId]
    );

    const editor = useSingleTypeEditor<ResearchBlogEntry, ResearchDetailEditable, ResearchBlogEntryInput>({
        schema: ResearchDetailSchema,
        loadEntity,
        update,
        toEditable: editableFromResearchBlogEntry,
        toInput: researchDetailInputFrom,
    });

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <SiteResearchDetailEditorContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={editor.discardChanges}
            />
        </SiteResearchDetailEditorContext.Provider>
    );
}
